import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { container } from '@/app/container';
import { UsersRepositoryToken } from '../../di/tokens';
import type {
  IdentityEntityType,
  IdentityEvent,
} from '../../domain/entities/user.entity';

/**
 * As entidades auditáveis que compõem o feed. `GrantedPerk` não é do Identity:
 * é a coleção do Delivery (`delivery_entity_events`), lida por uma rota própria
 * — é ela que responde "quem deu essa role".
 *
 * É um **subconjunto** de `IdentityEntityType`, não a união inteira: o Identity
 * também audita `AuthProviderIntent` e `OperationalAlert`, que não pertencem a
 * pessoa nenhuma — são a fila de reconciliação do provedor de auth e os alertas
 * dela, endereçados pela Central de Pendências. Deixá-los aqui obrigaria os
 * mapas de rótulo a carregar duas entradas que o feed nunca produz. O `Extract`
 * mantém o vínculo: renomear um valor lá quebra aqui.
 */
export type UserTimelineSource =
  | Extract<IdentityEntityType, 'Account' | 'User' | 'PlatformProfile'>
  | 'GrantedPerk';

export interface UserTimelineEvent extends IdentityEvent {
  source: UserTimelineSource;
  /** Só nos eventos de perk; `null` quando o perk saiu do catálogo. */
  perkKey?: string | null;
}

/** Uma query do feed: de onde vem e a que entidade se refere. */
interface TimelineSource {
  source: UserTimelineSource;
  id: string;
}

/**
 * Combina os `EntityEvent` de Account, User, de **cada perfil de plataforma** e
 * as concessões/revogações de perk **de cada perfil** num único feed cronológico
 * (mais recentes primeiro). Cada lado é buscado com o mesmo `perPage` —
 * "carregar mais" busca mais de todos e remonta o feed.
 *
 * Os perfis entram como uma query por perfil porque os dois endpoints endereçam
 * um id de cada vez; sem eles, criar/ativar/desativar perfil — e dar ou tirar
 * acesso — não apareceria em lugar nenhum.
 */
export function useUserEntityEvents(
  accountId: string,
  userId: string,
  platformProfileIds: string[],
  perPage: number,
  enabled = true,
) {
  const repository = container.resolve(UsersRepositoryToken);

  const sources: TimelineSource[] = [
    { source: 'Account', id: accountId },
    { source: 'User', id: userId },
    ...platformProfileIds.map((id) => ({
      source: 'PlatformProfile' as const,
      id,
    })),
    ...platformProfileIds.map((id) => ({ source: 'GrantedPerk' as const, id })),
  ];

  const results = useQueries({
    queries: sources.map(({ source, id }) => ({
      queryKey: ['users', 'entity-events', source, id, perPage],
      queryFn: () =>
        source === 'GrantedPerk'
          ? repository.getProfilePerkEvents(id, { page: 1, perPage })
          : repository.getEntityEvents(source, id, { page: 1, perPage }),
      enabled: !!id && enabled,
      staleTime: 1000 * 60 * 5,
      placeholderData: keepPreviousData,
      retry: false,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const isFetching = results.some((r) => r.isFetching);
  const isError = results.some((r) => r.isError);

  const events: UserTimelineEvent[] = results
    .flatMap((result, index) =>
      (result.data?.items ?? []).map((event) => ({
        ...event,
        source: sources[index].source,
      })),
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const total = results.reduce((sum, r) => sum + (r.data?.total ?? 0), 0);
  const hasMore = events.length < total;

  return { events, total, hasMore, isLoading, isFetching, isError };
}
